import type { NavigationGuardReturn, RouteLocationNormalized } from "vue-router";

import { initSession, loadSessionData } from "@/backend/read/BackendReader";
import { DisconnectedError } from "@/error/DisconnectedError";
import { handleError } from "@/error/errorHandler";
import { isReloading, reload } from "@/error/reload";
import { addBreadcrumb } from "@/error/sentry";
import type { Session } from "@/model/session";
import { emptyBoard, useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useGlobalErrorStore } from "@/store/globalError";
import { useLoadingStore } from "@/store/loading";
import { useNavigationStore } from "@/store/navigation";
import { EMPTY_SESSION, useSessionStore } from "@/store/session";
import { useUserStore } from "@/store/user";
import { untilBrowserTabVisible } from "@/utils/dom/dom";
import { isExecuting, isPlanning } from "@/utils/session";

import { setSessionData } from "../sessionData";
import { PARAM } from "../types";
import { uniqueActiveSession } from "./appNavigation";
import { replaceParams, replaceRoute } from "./utils";

export async function handleSessionParam(
  to: RouteLocationNormalized,
): Promise<NavigationGuardReturn> {
  if (to.name !== "app" || useGlobalErrorStore().globalError) {
    return;
  }

  // there can be only on session param, cast is ok
  const toSession = to.params.session as string;

  // no session at all -> use dummy session
  if (toSession === PARAM.NONE) {
    if (to.query.modal !== "SessionSelectionModal") {
      return replaceRoute(to, { query: { modal: "SessionSelectionModal" } });
    }
    useSessionStore().setCurrent(EMPTY_SESSION);
    const empty = emptyBoard();
    useBoardStore().setBoard(empty);
    useBoardsStore().setBoards({ [empty.id]: empty });
    useLoadingStore().end({ expectAllItems: false });
    return;
  }

  const sessions = useSessionStore().sessions;
  if (sessions.length === 0) {
    return noSession(to);
  }
  const latestSession =
    sessions.find((session) => isPlanning(session) || isExecuting(session)) || sessions[0];

  let session;
  const toName = toSession === PARAM.LATEST ? latestSession.name : toSession;

  if (toSession === PARAM.INITIAL) {
    if (to.query.modal !== "SessionSelectionModal") {
      const active = await uniqueActiveSession(useUserStore().technicalUser);
      // user has one active session -> go directly there
      if (active) {
        return replaceParams(to, { session: useSessionStore().uniqueSessionName(active) });
      }
      // go to the last used session and show session selection modal
      return replaceRoute(to, { query: { modal: "SessionSelectionModal" } });
    }
    session = lastUsedSession();
  } else if (useSessionStore().currentlyDifferentSession(toName)) {
    session = sessionByNameOrId(toName);
  } else {
    session = useSessionStore().current;
  }
  session = session || latestSession;

  // no session found -> empty/dummy session
  if (!session || session.id === EMPTY_SESSION.id) {
    return noSession(to);
  }

  const name =
    session === latestSession ? PARAM.LATEST : useSessionStore().uniqueSessionName(session);
  if (toSession !== name) {
    return replaceParams(to, { session: name });
  }

  if (session.id !== useSessionStore().current.id) {
    try {
      useSessionStore().setLoading(true);
      await loadSession(session);
    } catch (fail) {
      useSessionStore().setLoading(false);
      if (fail instanceof DisconnectedError) {
        // session load cancelled by a lost/stale connection (typically a backgrounded tab).
        // The app would hang if we try to continue (empty board store), so reload like reconnect() does.
        // Note: if this fires during an in-app session switch, the previous session's URL will be reloaded. Not ideal, but acceptable.
        if (!isReloading()) {
          await untilBrowserTabVisible();
          reload("session-load-cancelled");
        }
        return false;
      }
      return handleError(fail);
    }
  }
}

function noSession(to: RouteLocationNormalized) {
  return replaceParams(to, { session: PARAM.NONE, board: PARAM.NONE, team: PARAM.NONE });
}

async function loadSession(session: Session) {
  addBreadcrumb("navigation", {
    message: "Changing session",
    data: { id: session.id, name: session.name },
  });
  useSessionStore().setSession(session);
  await initSession(session.id);
  await useLoadingStore().load("session", loadSessionData(session).then(setSessionData));
}

function lastUsedSession(): Session | undefined {
  const lastSessionId = useNavigationStore().lastSelectedSessionId();
  if (lastSessionId) {
    const lastSession = useSessionStore().findByNameOrId(lastSessionId);
    if (lastSession) {
      return sessionByNameOrId(lastSession.name);
    }
  }
}

function sessionByNameOrId(target: string): Session | undefined {
  return useSessionStore().findByNameOrId(target) || useSessionStore().findByIndexedName(target);
}
